import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import Input from 'src/components/Input/Input';
import * as yup from 'yup';
import { setBigin } from 'src/store/slices/onboardingSlice';
import { PATH_ONBOARDING } from 'src/constants/spa-routes';
import { useAppSelector } from 'src/store';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from 'src/components/ui/button';
import { useLoader } from 'src/provider/LoaderProvider';
import { ProductsType } from 'src/constants/products';

const REQUESTS = [
  { id: 1, value: '1000', label: 'Less than 1,000' },
  { id: 2, value: '5000', label: '1,000 – 5,000' },
  { id: 3, value: '10000', label: '5,000 – 10,000' },
  { id: 4, value: '15000', label: '10,000+' },
];
const CRMS = [
  { id: 1, value: 'Zendesk', label: 'Zendesk' },
  { id: 2, value: 'Freshdesk', label: 'Freshdesk' },
  { id: 3, value: 'Intercom', label: 'Intercom' },
  { id: 4, value: 'Zoho Desk', label: 'Zoho Desk' },
  { id: 5, value: 'Other', label: 'Other' },
  { id: 6, value: 'none', label: 'None' },
];

const OnboardingStep0 = () => {
  const otherRef = useRef(null);
  const { bigin } = useAppSelector((s) => s.onboardingSlice);
  const loader = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!bigin.pipelineId) {
      navigate(`${PATH_ONBOARDING.ROOT}/1?type=${ProductsType.customer}`);
    }

    loader.hide();
  }, []);

  const {
    watch,
    handleSubmit,
    control,
    setValue,
    formState: { isDirty, isValid },
  } = useForm({
    defaultValues: {
      Requests: bigin?.data?.Requests || '1000',
      CRM: bigin?.data?.CRM
        ? CRMS.find((crm) => crm.value === bigin?.data?.CRM)?.value || 'Other'
        : 'Zendesk',
      other: bigin?.data?.CRM
        ? CRMS.find((crm) => crm.value === bigin?.data?.CRM)?.value
          ? ''
          : bigin?.data?.CRM
        : '',
    },
    resolver: yupResolver(
      yup.lazy((values) => {
        return object({
          Requests: stringScheme({ required: true }).label('What is your monthly ticket volume?'),
          CRM: stringScheme({ required: true }).label('Which help desk do you use?'),
          other: stringScheme({ required: values.CRM === 'Other' }),
        });
      }),
    ),
  });

  const crm = watch('CRM');

  useEffect(() => {
    if (crm === 'Other') {
      otherRef?.current?.focus();
    } else {
      setValue('other', '');
    }
  }, [crm, otherRef]);

  const submit = async (data) => {
    const body = {
      id: bigin.pipelineId,
      Requests: data.Requests,
      CRM: data.CRM === 'Other' ? data.other : data.CRM,
    };
    loader.show();
    const response = await fetch('/api/pipeline', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then((res) => res.json());

    if (response.success) {
      loader.hide();
      dispatch(
        setBigin({ ...bigin, data: { ...bigin.data, CRM: body.CRM, Requests: body.Requests } }),
      );

      navigate(`${PATH_ONBOARDING.ROOT}/1?type=${ProductsType.customer}`);
    }
  };

  return (
    <div>
      <form id={'update-bigin-pipeline'} onSubmit={handleSubmit(submit)}>
        <div className={'flex flex-col gap-5 w-[500px]'}>
          <div className={'flex flex-col gap-2.5'}>
            <p className={'text-ebony_opacity_80 font-semibold text-sm'}>
              What is your monthly ticket volume?
            </p>
            <Controller
              control={control}
              name="Requests"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <RadioGroup
                  onChange={onChange}
                  onValueChange={onChange}
                  defaultValue={value}
                  className="flex flex-col space-y-1 gap-4"
                >
                  {REQUESTS.map((request) => (
                    <div key={request.id} className="flex items-center space-x-5 space-y-0">
                      <RadioGroupItem value={request.value} id={`${request.id}-${request.value}`} />
                      <label htmlFor={`${request.id}-${request.value}`} className="cursor-pointer">
                        <p className="font-medium text-sm text-ebony opacity-50 hover:opacity-100 transition">
                          {request.label}
                        </p>
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            />
          </div>
          <div className={'flex flex-col gap-2.5'}>
            <p className={'text-ebony_opacity_80 font-semibold text-sm'}>
              Which help desk do you use?
            </p>
            <Controller
              control={control}
              name="CRM"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <RadioGroup
                  onChange={onChange}
                  onValueChange={onChange}
                  defaultValue={value}
                  className="flex flex-col space-y-1 gap-4"
                >
                  {CRMS.map((item) => (
                    <div key={item.id} className="flex flex-col">
                      <div className="flex items-center space-x-5 space-y-0">
                        <RadioGroupItem value={item.value} id={`${item.id}-${item.value}`} />
                        <label htmlFor={`${item.id}-${item.value}`} className="cursor-pointer">
                          <p className="font-medium text-sm text-ebony opacity-50 hover:opacity-100 transition">
                            {item.label}
                          </p>
                        </label>
                      </div>
                      {item.value === 'Other' && crm === 'Other' && (
                        <Controller
                          control={control}
                          name="other"
                          render={({
                            field: { onChange: onOtherChange, value: otherValue },
                            fieldState: { error: otherError },
                          }) => (
                            <Input
                              ref={otherRef}
                              readonly={crm !== 'Other'}
                              type={'text'}
                              placeholder={'Your Help Desk'}
                              invalidMessage={otherError?.message}
                              value={otherValue}
                              isValid={!otherError?.message}
                              onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onOtherChange(e);
                              }}
                              className={'!py-2 !px-3.5 mt-2.5'}
                            />
                          )}
                        />
                      )}
                    </div>
                  ))}
                </RadioGroup>
              )}
            />
          </div>
        </div>
      </form>
      <div className={'mt-5'}>
        <Button
          form="update-bigin-pipeline"
          disabled={!isValid}
          className="w-full py-6 font-semibold"
        >
          <p>Continue</p>
        </Button>
      </div>
    </div>
  );
};

export default OnboardingStep0;
